/**
 * RBAC API Layer
 * Client-side API wrapper and TanStack Query hooks for RBAC endpoints.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from './queryClient';

// ============================================================================
// Types (mirroring server-nest/src/modules/rbac/rbac.types.ts)
// ============================================================================

export type ModuleAccessLevel = 'none' | 'read' | 'full';
export type OverrideSource = 'manual' | 'flag';

export interface ModuleRegistryEntry {
  moduleKey: string;
  displayName: string;
  description: string;
  category: string;
  featureFlagHint?: string;
}

export interface RoleModulePermissionEntry {
  moduleKey: string;
  accessLevel: ModuleAccessLevel;
  enforcedByFlag: string | null;
}

export interface RoleWithPermissions {
  id: number;
  slug: string;
  displayName: string;
  description: string | null;
  scope: string | null;
  requiresAuth0Sync: boolean;
  permissions: RoleModulePermissionEntry[];
}

export interface UserOverrideEntry {
  moduleKey: string;
  accessLevel: ModuleAccessLevel;
  source: OverrideSource;
}

export interface UserRbacDetails {
  userId: number;
  email: string;
  displayName: string;
  primaryRole: {
    id: number;
    slug: string;
    displayName: string;
  } | null;
  effectivePermissions: Record<string, ModuleAccessLevel>;
  overrides: UserOverrideEntry[];
  threadsAdvancedEnabled: boolean;
}

export interface UpdateUserRbacPayload {
  roleId?: number;
  moduleOverrides?: Array<{
    moduleKey: string;
    accessLevel: ModuleAccessLevel;
  }>;
  threadsAdvancedEnabled?: boolean;
  reason?: string;
}

export interface UpdateUserRbacResponse {
  success: boolean;
  user: UserRbacDetails;
  audit: {
    action: string;
    timestamp: string;
  };
}

// ============================================================================
// API Functions
// ============================================================================

/** Fetch all roles with their default permissions */
export async function getRbacRoles(): Promise<{ roles: RoleWithPermissions[] }> {
  return apiRequest<{ roles: RoleWithPermissions[] }>('GET', '/api/admin/rbac/roles');
}

/** Fetch the module registry */
export async function getRbacModules(): Promise<{ modules: ModuleRegistryEntry[] }> {
  return apiRequest<{ modules: ModuleRegistryEntry[] }>('GET', '/api/admin/rbac/modules');
}

/** Fetch RBAC details for a specific user */
export async function getUserRbac(userId: number): Promise<UserRbacDetails> {
  return apiRequest<UserRbacDetails>('GET', `/api/admin/rbac/users/${userId}`);
}

/** Update a user's RBAC settings */
export async function updateUserRbac(
  userId: number,
  payload: UpdateUserRbacPayload
): Promise<UpdateUserRbacResponse> {
  return apiRequest<UpdateUserRbacResponse>('PATCH', `/api/admin/rbac/users/${userId}`, payload);
}

// ============================================================================
// Query Keys
// ============================================================================

export const rbacQueryKeys = {
  all: ['rbac'] as const,
  roles: () => [...rbacQueryKeys.all, 'roles'] as const,
  modules: () => [...rbacQueryKeys.all, 'modules'] as const,
  user: (userId: number) => [...rbacQueryKeys.all, 'user', userId] as const,
};

// ============================================================================
// TanStack Query Hooks
// ============================================================================

/** Hook to fetch all roles */
export function useRbacRoles() {
  return useQuery({
    queryKey: rbacQueryKeys.roles(),
    queryFn: getRbacRoles,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/** Hook to fetch module registry */
export function useRbacModules() {
  return useQuery({
    queryKey: rbacQueryKeys.modules(),
    queryFn: getRbacModules,
    staleTime: 10 * 60 * 1000, // 10 minutes - modules rarely change
  });
}

/** Hook to fetch a user's RBAC details */
export function useUserRbac(userId: number | null) {
  return useQuery({
    queryKey: rbacQueryKeys.user(userId!),
    queryFn: () => getUserRbac(userId!),
    enabled: userId !== null,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/** Hook to update a user's RBAC settings with optimistic updates */
export function useUpdateUserRbac() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, payload }: { userId: number; payload: UpdateUserRbacPayload }) =>
      updateUserRbac(userId, payload),

    onSuccess: (data, { userId }) => {
      // Derive role/advanced from response with broad fallbacks to match backend shape
      const newRoleSlug =
        data?.user?.primaryRole?.slug ??
        (data as any)?.user?.roleSlug ??
        (data as any)?.primaryRoleSlug ??
        (data as any)?.roleSlug ??
        (data as any)?.user?.role ??
        (data as any)?.role ??
        null;
      const newThreadsAdvanced =
        data?.user?.threadsAdvancedEnabled ??
        (data as any)?.threadsAdvancedEnabled ??
        (data as any)?.user?.threadsAdvanced ??
        null;

      // Optimistically update any cached admin users lists so UI reflects the change immediately
      const adminUserQueries = queryClient.getQueriesData<{
        users: Array<{ id: number; role?: string; roleSlug?: string; threadsAdvancedEnabled?: boolean }>;
        total?: number;
        page?: number;
        limit?: number;
        totalPages?: number;
      }>({
        predicate: (q) =>
          Array.isArray(q.queryKey) && q.queryKey[0] === '/api/admin/users',
      });

      adminUserQueries.forEach(([key, value]) => {
        if (!value?.users) return;
        const updatedUsers = value.users.map((u) =>
          u.id === userId
            ? {
                ...u,
                roleSlug: newRoleSlug ?? u.roleSlug ?? u.role,
                role: newRoleSlug ?? u.role,
                threadsAdvancedEnabled:
                  newThreadsAdvanced !== null && newThreadsAdvanced !== undefined
                    ? newThreadsAdvanced
                    : u.threadsAdvancedEnabled,
              }
            : u,
        );
        queryClient.setQueryData(key, { ...value, users: updatedUsers });
      });
    },

    onMutate: async ({ userId, payload }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: rbacQueryKeys.user(userId) });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<UserRbacDetails>(rbacQueryKeys.user(userId));

      // Optimistically update the cache
      if (previousData) {
        queryClient.setQueryData<UserRbacDetails>(rbacQueryKeys.user(userId), {
          ...previousData,
          threadsAdvancedEnabled: payload.threadsAdvancedEnabled ?? previousData.threadsAdvancedEnabled,
          // Note: We don't optimistically update role changes since they're complex
        });
      }

      return { previousData, userId };
    },

    onError: (_err, { userId }, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(rbacQueryKeys.user(userId), context.previousData);
      }
    },

    onSettled: (_data, _error, { userId }) => {
      // Refetch the detailed RBAC view for this user
      queryClient.invalidateQueries({ queryKey: rbacQueryKeys.user(userId) });
      // Do NOT invalidate /api/admin/users here; we rely on patched cache to avoid reverting to stale roles
    },
  });
}

// ============================================================================
// Helper Functions
// ============================================================================

/** Get a human-readable label for an access level */
export function getAccessLevelLabel(level: ModuleAccessLevel): string {
  switch (level) {
    case 'none':
      return 'No Access';
    case 'read':
      return 'Read Only';
    case 'full':
      return 'Full Access';
    default:
      return 'Unknown';
  }
}

/** Get badge variant for access level */
export function getAccessLevelVariant(level: ModuleAccessLevel): 'destructive' | 'secondary' | 'default' {
  switch (level) {
    case 'none':
      return 'destructive';
    case 'read':
      return 'secondary';
    case 'full':
      return 'default';
    default:
      return 'secondary';
  }
}

/** Check if a role change would be a downgrade */
export function isRoleDowngrade(
  currentRoleSlug: string | undefined,
  newRoleSlug: string
): boolean {
  const hierarchy = ['SYSTEM_ADMIN', 'AGENCY_OWNER', 'AGENCY_MANAGER', 'ADVISOR', 'READ_ONLY'];
  const currentIndex = hierarchy.indexOf(currentRoleSlug ?? '');
  const newIndex = hierarchy.indexOf(newRoleSlug);
  return newIndex > currentIndex && currentIndex !== -1;
}

/** Check if an override is valid for a given role */
export function isValidOverride(
  roleSlug: string | undefined,
  accessLevel: ModuleAccessLevel
): boolean {
  // READ_ONLY users cannot receive 'full' access overrides
  if (roleSlug === 'READ_ONLY' && accessLevel === 'full') {
    return false;
  }
  return true;
}

